/**
 * What an SBOM upload onto a product or a product instance will do (DEV-107),
 * shown above the review. For an instance whose scanned configuration deviates
 * from every known version, the operator chooses here between a custom
 * version and a new official version.
 */

import { useEffect, useId, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { countLabel } from '../../lib/format';
import type {
  InstanceImportTarget,
  InstanceVersionChoice,
  ProductImportTarget,
} from '../../lib/types';

function ProductSummary({ target }: { target: ProductImportTarget }): ReactNode {
  const affected = target.mode === 'update' ? target.affected_instances : 0;
  return (
    <>
      <Banner
        kind={affected > 0 ? 'warn' : 'info'}
        sub={
          affected > 0
            ? `Die geänderte Stückliste gilt für ${countLabel(affected, 'Produktinstanz', 'Produktinstanzen')}, die auf dieser Version laufen.`
            : undefined
        }
      >
        {target.mode === 'new'
          ? <>Neue offizielle Version <b>{target.version.version_nr}</b> von <b>{target.product.name}</b> wird angelegt.</>
          : <>Version <b>{target.version.version_nr}</b> von <b>{target.product.name}</b> wird aktualisiert.</>}
      </Banner>
      {target.equals_versions.length > 0 ? (
        <Banner kind="warn" sub="Prüfen Sie, ob Sie wirklich eine weitere Version mit gleicher Stückliste brauchen.">
          Die gescannte Konfiguration entspricht bereits{' '}
          {target.equals_versions
            .map((v) => `Version ${v.version_nr}${v.custom ? ' (Kundenversion)' : ''}`)
            .join(', ')}.
        </Banner>
      ) : null}
    </>
  );
}

interface InstanceSummaryProps {
  target: InstanceImportTarget;
  disabled: boolean;
  onChoice: (choice: InstanceVersionChoice | null) => void;
}

function InstanceSummary({ target, disabled, onChoice }: InstanceSummaryProps): ReactNode {
  const versions = target.official_versions;
  const idPrefix = useId();
  const [mode, setMode] = useState<'custom' | 'official'>('custom');
  const [versionNr, setVersionNr] = useState('');
  const [predecessorId, setPredecessorId] = useState<number | null>(target.base_version.id);

  const taken = new Set(versions.map((v) => v.version_nr.trim().toLowerCase()));
  const trimmed = versionNr.trim();
  const duplicate = trimmed !== '' && taken.has(trimmed.toLowerCase());

  useEffect(() => {
    if (target.outcome !== 'deviation') {
      onChoice(null);
    } else if (mode === 'custom') {
      onChoice({ mode: 'custom' });
    } else if (trimmed !== '' && !duplicate) {
      onChoice({ mode: 'official', version_nr: trimmed, predecessor_id: predecessorId ?? undefined });
    } else {
      onChoice(null);
    }
  }, [target.outcome, mode, trimmed, duplicate, predecessorId, onChoice]);

  const instance = `${target.product.name} ${target.instance.serial}`.trim();
  const current = `Version ${target.current_version.version_nr}${target.current_version.custom ? ' (Kundenversion)' : ''}`;
  const { counts } = target;
  const differences = [
    counts.changed > 0 ? countLabel(counts.changed, 'geänderte Komponente', 'geänderte Komponenten') : null,
    counts.added > 0 ? countLabel(counts.added, 'neue Komponente', 'neue Komponenten') : null,
    counts.missing > 0 ? `${countLabel(counts.missing, 'Komponente fehlt', 'Komponenten fehlen')} im Scan (bleiben, sofern nicht entfernt)` : null,
  ].filter((part): part is string => part !== null);

  return (
    <>
      <Banner kind="info" sub={differences.length > 0 ? `Gegenüber ${current}: ${differences.join(', ')}.` : undefined}>
        Scan von <b>{instance}</b>{target.instance.customer ? <> ({target.instance.customer})</> : null},
        läuft derzeit auf <b>{current}</b>.
      </Banner>

      {target.outcome === 'identical' ? (
        <Banner kind="ok">Die gescannte Konfiguration entspricht der aktuellen Version — die Version der Instanz bleibt.</Banner>
      ) : null}

      {target.outcome === 'switch' && target.switch_to !== null ? (
        <Banner kind="info" sub="Es wird keine neue Version angelegt.">
          Die gescannte Konfiguration entspricht{' '}
          <b>Version {target.switch_to.version_nr}{target.switch_to.custom ? ' (Kundenversion)' : ''}</b> —
          die Instanz wird beim Übernehmen darauf umgestellt.
        </Banner>
      ) : null}

      {target.outcome === 'deviation' ? (
        <section className="imp-target" aria-labelledby={`${idPrefix}-title`}>
          <h2 id={`${idPrefix}-title`}>Die Konfiguration weicht von allen Versionen ab</h2>
          <div className="seg imp-target-mode" role="radiogroup" aria-label="Neue Version">
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'custom'}
              className={mode === 'custom' ? 'on' : undefined}
              disabled={disabled}
              onClick={() => setMode('custom')}
            >
              Kundenversion anlegen
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'official'}
              className={mode === 'official' ? 'on' : undefined}
              disabled={disabled}
              onClick={() => setMode('official')}
            >
              Neue offizielle Version anlegen
            </button>
          </div>
          {mode === 'custom' ? (
            <p className="imp-target-note">
              Kennung <b>{target.custom_version_nr}</b>, basiert auf Version {target.base_version.version_nr} und gilt
              nur für diese Produktinstanz.
            </p>
          ) : (
            <div className="imp-target-fields">
              <label htmlFor={`${idPrefix}-nr`}>
                Versionsnummer
                <input
                  id={`${idPrefix}-nr`}
                  type="text"
                  value={versionNr}
                  disabled={disabled}
                  required
                  aria-invalid={duplicate || undefined}
                  aria-describedby={duplicate ? `${idPrefix}-nr-error` : undefined}
                  onChange={(event) => setVersionNr(event.target.value)}
                  placeholder="z. B. 1.3"
                />
              </label>
              {duplicate ? (
                <p id={`${idPrefix}-nr-error`} className="imp-target-error">Version {trimmed} existiert bereits.</p>
              ) : null}
              <label htmlFor={`${idPrefix}-pred`}>
                Vorgängerversion (optional)
                <select
                  id={`${idPrefix}-pred`}
                  value={predecessorId ?? ''}
                  disabled={disabled}
                  onChange={(event) => setPredecessorId(event.target.value === '' ? null : Number(event.target.value))}
                >
                  <option value="">Keine</option>
                  {versions.map((v) => <option key={v.id} value={v.id}>{v.version_nr}</option>)}
                </select>
              </label>
            </div>
          )}
          <p className="imp-target-note">
            Ändern Sie unten Entscheidungen, prüft der Server beim Übernehmen erneut, ob eine gleiche Version
            bereits existiert, und stellt die Instanz dann darauf um.
          </p>
        </section>
      ) : null}
    </>
  );
}

interface TargetSummaryProps {
  target: ProductImportTarget | InstanceImportTarget;
  disabled: boolean;
  onChoice: (choice: InstanceVersionChoice | null) => void;
}

export function TargetSummary({ target, ...props }: TargetSummaryProps): ReactNode {
  return target.kind === 'product'
    ? <ProductSummary target={target} />
    : <InstanceSummary target={target} {...props} />;
}
